<template>
  <div class="trt-card" :class="{ expanded }">
    <!-- Collapsed: name + status pill (click to toggle) -->
    <div class="trt-card-head" @click="expanded = !expanded">
      <span class="trt-card-name">{{ fullName }}</span>
      <Tooltip v-if="status.tooltip" :text="status.tooltip">
        <span class="trt-status-pill" :class="status.cls">{{ status.label }}</span>
      </Tooltip>
      <span v-else class="trt-status-pill" :class="status.cls">{{ status.label }}</span>
      <ChevronDownIcon :size="18" class="trt-card-caret" :class="{ open: expanded }" />
    </div>

    <!-- Expanded: detail table -->
    <table v-if="expanded" class="trt-table" @click.stop>
      <tbody>
        <tr>
          <td class="trt-table-label">Dose</td>
          <td class="trt-table-value">
            <span v-if="dose.isNone" class="trt-muted">None</span>
            <span v-else>{{ dose.label }}</span>
          </td>
        </tr>
        <tr>
          <td class="trt-table-label">Monitor Date</td>
          <td class="trt-table-value">
            <a class="trt-monitor-link" @click="$emit('edit-monitor-date', record)">{{ monitor.label }}</a>
          </td>
        </tr>
        <tr v-for="row in monitorRows" :key="row.label">
          <td class="trt-table-label">{{ row.label }}</td>
          <td class="trt-table-value">
            <Tooltip v-if="row.tooltip" :text="row.tooltip">
              <span :class="{ 'trt-muted': row.muted }">{{ row.value }}</span>
            </Tooltip>
            <span v-else :class="{ 'trt-muted': row.muted }">{{ row.value }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import * as formatUtils from '@/utils/formatters';
import {
  getTestosteroneMedication,
  getTrtDoseStatus,
  formatTrtDose,
  getMostRecentAdamDocument,
  getMostRecentVitals,
  getTestosteroneLabStatus,
  getMonitorReadiness,
  daysUntil,
  MONITOR_WINDOW_DAYS
} from '@/utils/trt';

export default {
  name: 'TRTPatientCard',
  components: { ChevronDownIcon, Tooltip },
  props: {
    record: {
      type: Object,
      required: true
    }
  },
  emits: ['edit-monitor-date'],
  data() {
    return {
      expanded: false
    };
  },
  computed: {
    fullName() {
      return `${this.record.firstName || ''} ${this.record.lastName || ''}`.trim() || 'Unknown';
    },
    testMed() {
      return getTestosteroneMedication(this.record.trtData);
    },
    // Collapsed-header pill. Past/none show the dose status; on a dose it shows
    // the days-until-monitor countdown, color-coded green/orange/red by urgency.
    //
    // Once the monitor date is inside the window the pill switches to whether
    // the patient is actually ready for that visit: "Ready" only when all three
    // monitoring items are in hand, otherwise "Missing n/3" in orange so the
    // gap is visible without expanding the card.
    status() {
      const doseStatus = getTrtDoseStatus(this.record.trtData);
      if (doseStatus === 'none') return { label: 'None', cls: 'pill-gray' };
      if (doseStatus === 'past') return { label: 'Past', cls: 'pill-gray' };
      const days = daysUntil(this.record.monitorDate);
      if (days === null) return { label: 'No date', cls: 'pill-red' };
      if (days <= 0) return { label: 'Overdue', cls: 'pill-red', tooltip: this.missingTooltip };
      if (days > MONITOR_WINDOW_DAYS) return { label: `${days}d`, cls: 'pill-green' };
      const { complete, missing, total } = this.readiness;
      if (complete) return { label: 'Ready', cls: 'pill-green' };
      return { label: `Missing ${missing}/${total}`, cls: 'pill-orange', tooltip: this.missingTooltip };
    },
    // Vitals / qADAM / labs, with whether each counts as collected for the
    // upcoming monitor date. Shared by the status pill and the detail rows.
    readiness() {
      return getMonitorReadiness(this.record);
    },
    // "Needs: Vitals, Labs" — what the Missing pill is counting.
    missingTooltip() {
      const names = this.readiness.items.filter((i) => !i.collected).map((i) => i.label);
      return names.length ? `Needs: ${names.join(', ')}` : '';
    },
    // Dose row — just the cc value (or None).
    dose() {
      if (getTrtDoseStatus(this.record.trtData) === 'none') return { isNone: true };
      return { isNone: false, label: formatTrtDose(this.testMed) };
    },
    // The next monitor date (user-set), shown as a countdown; opens the modal.
    monitor() {
      const md = this.record.monitorDate;
      if (!md) return { label: 'Set date' };
      const days = daysUntil(md);
      if (days === null) return { label: formatUtils.formatDate(md) };
      if (days === 0) return { label: 'Today' };
      if (days > 0) return { label: `in ${days} day${days === 1 ? '' : 's'}` };
      return { label: `${-days} day${days === -1 ? '' : 's'} overdue` };
    },
    // Vitals / qADAM / Labs, shown as relative dates ("12 days ago" / "Never").
    // Anything that doesn't count toward readiness — missing, stale, or a lab
    // that's only been ordered — is muted, so the muted rows are exactly the
    // ones the "Missing n/3" pill counts.
    monitorRows() {
      const vitals = getMostRecentVitals(this.record.trtData);
      const doc = getMostRecentAdamDocument(this.record.trtData);
      const lab = getTestosteroneLabStatus(this.record.trtData);
      const tooltips = {
        vitals: vitals ? this.vitalsTooltip(vitals) : '',
        qadam: doc && doc.name ? doc.name : '',
        labs: lab.status === 'none' ? '' : this.labTooltip(lab.status === 'received' ? lab.result : lab.request)
      };

      return this.readiness.items.map((item) => {
        if (!item.date) return { label: item.label, value: 'Never', muted: true, tooltip: '' };
        const value = item.pending ? `${this.relDays(item.date)} (pending)` : this.relDays(item.date);
        return { label: item.label, value, muted: !item.collected, tooltip: tooltips[item.key] };
      });
    }
  },
  methods: {
    // Relative past-form for the Upcoming column, e.g. "12 days ago", "Today".
    relDays(iso) {
      const days = daysUntil(iso);
      if (days === null) return '';
      if (days === 0) return 'Today';
      if (days < 0) return `${-days} day${days === -1 ? '' : 's'} ago`;
      return `in ${days} day${days === 1 ? '' : 's'}`;
    },
    // Short tooltip for a vitals reading: the date, then one line per
    // measurement, e.g. "BP: 129/83 mmHg".
    vitalsTooltip(reading) {
      const measurements = Array.isArray(reading.measurements) ? reading.measurements : [];
      const lines = measurements
        .filter((m) => m.value)
        .map((m) => `${m.name}: ${m.value}${m.units ? ` ${m.units}` : ''}`);
      if (reading.recordedDate) lines.unshift(formatUtils.formatDate(reading.recordedDate));
      return lines.join('\n');
    },
    // Short tooltip for a lab order/result: lab name, dates, status.
    labTooltip(lab) {
      if (!lab) return '';
      const lines = [];
      if (lab.labName) lines.push(lab.labName);
      if (lab.orderedDate) lines.push(`Ordered: ${formatUtils.formatDate(lab.orderedDate)}`);
      if (lab.receivedDate) lines.push(`Received: ${formatUtils.formatDate(lab.receivedDate)}`);
      if (lab.status) lines.push(`Status: ${lab.status}`);
      return lines.join('\n');
    }
  }
};
</script>

<style scoped>
.trt-card {
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: var(--border-radius);
  overflow: hidden;
  user-select: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.trt-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* --- Collapsed header --------------------------------------------------- */
.trt-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
}

.trt-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trt-status-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 999px;
  white-space: nowrap;
}

.trt-card-caret {
  display: inline-flex;
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.15s ease-in-out;
}

.trt-card-caret.open {
  transform: rotate(180deg);
}

/* --- Expanded body ------------------------------------------------------ */
.trt-table {
  width: 100%;
  border-collapse: collapse;
  border-top: 1px solid #eef0f2;
  table-layout: fixed;
}

.trt-table td {
  padding: 9px 14px;
  font-size: 12px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.trt-table tr:last-child td {
  border-bottom: none;
}

.trt-table-label {
  width: 44%;
  color: #6b7280;
  white-space: nowrap;
}

.trt-table-value {
  text-align: right;
  color: #1f2937;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dimmed value — used for "None" / "Never" and stale dates */
.trt-muted {
  color: #9ca3af;
  font-weight: 400;
}

/* Clickable monitor-date value */
.trt-monitor-link {
  color: var(--primary-color);
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.trt-monitor-link:hover {
  color: var(--primary-hover);
}

/* --- Status pill tones -------------------------------------------------- */
.pill-green {
  background-color: #d6eba3;
  color: #2f5d18;
}
.pill-orange {
  background-color: #fbd7a6;
  color: #8a4b06;
}
.pill-red {
  background-color: #f6c5cb;
  color: #6f1b22;
}
.pill-gray {
  background-color: #eceef0;
  color: #6b7280;
}
</style>
